<template lang="pug">
div.surface(v-if="title")
  VContainer
    VRow
      VCol
        HH.text-h1 {{ title }}
VContainer.mb-4
  VRow
    VCol
      ErrorAlert(
        :message="message"
        :stack="stack"
        :stack-is-html="stackIsHtml"
        :status-code="statusCode"
        @retry="retry"
        @report="report"
      )
        slot

  // Calls to action.
VContainer
  VRow
    VCol.mh-0(
      :sm="6"
      :cols="12"
    )
      VCard.h-100(variant="outlined")
        VListItem.py-2.h-100(
          :to="localePath(getScriptureIndexBooksLocation())"
        )
          template(#prepend)
            VIcon(:color="dark ? 'secondary-c500' : 'secondary-c900'") {{ mdiViewGridOutline }}
          VListItemTitle {{ i18n.t("ScriptureIndex.ScriptureIndex") }}
          VListItemSubtitle {{ i18n.t("Error.ScriptureIndexCTA") }}

    VCol.mh-0(
      :sm="6"
      :cols="12"
    )
      VCard.h-100(variant="outlined")
        VListItem.py-2.h-100(:to="localePath('/')")
          template(#prepend)
            VIcon(:color="dark ? 'secondary-c500' : 'secondary-c900'") {{ mdiHomeOutline }}
          VListItemTitle {{ i18n.t("Error.GoHomeCTAButton") }}
          VListItemSubtitle {{ i18n.t("Error.GoHomeCTA") }}
</template>

<script setup lang="ts">
/**
 * Use this for a full-page error.
 */
import { useI18n } from "vue-i18n";
import { getScriptureIndexBooksLocation } from "~/routing/locations";
import { hasListener } from "~/composables/useProps";
import { mdiViewGridOutline, mdiHomeOutline } from "@mdi/js";
import HH from "~/headings/components/HH.vue";
import ErrorAlert from "~/components/ErrorAlert.vue";
import { useDarkMode } from "~/composables/useDarkMode";

const i18n = useI18n();
const localePath = useLocalePath();
const dark = useDarkMode();

interface Props {
  /**
   * The title of the error page.
   */
  title?: string;

  /**
   * The error message, as a string.
   * This is useful when we don't have a full `Error` object.
   */
  message?: string;

  /**
   * The error stack, as a string.
   * This is useful when we don't have a full `Error` object.
   */
  stack?: string;

  /**
   * Whether the stack is HTML. If `false`, the stack is treated as plain text and placed in a `<pre>` tag.
   */
  stackIsHtml?: boolean;

  /**
   * The HTTP status code to use if this component exists in SSR or pre-rendering.
   * The HTTP response for a page containing this component will have this status code.
   * A page with a status code >= 400
   * - should not be cached in CDNs or in browsers.
   * - should not be included in the pre-rendered build.
   */
  statusCode?: number;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Error",
});

interface Emits {
  (e: "retry"): void;
  (e: "report"): void;
}

const emit = defineEmits<Emits>();

useRobotsRule(false);

useHead({
  title: props.title,
});

const retry = hasListener("retry") ? () => emit("retry") : undefined;
const report = hasListener("report") ? () => emit("report") : undefined;
</script>

<style scoped lang="scss">
.the-error__stack {
  max-height: 50vh;
}
</style>
